import { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa'

import { getSchedulesOfRates, RatesCell, RatesSheet, SchedulesOfRates } from '../../data/schedulesOfRatesData'
import { getApiErrorDetails } from '../../helpers/apiError'
import './SchedulesOfRates.css'

const SLIDE_MS = 250

const formatTime = (value: string) =>
  new Date(value).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

/**
 * The rows kept in view at the top: the sheet's frozen rows, or else the first
 * row and any rows its merged cells reach down into.
 */
const headingCount = (sheet: RatesSheet) =>
  sheet.headingRows > 0
    ? sheet.headingRows
    : Math.max(1, ...(sheet.rows[0]?.cells ?? []).map((cell) => cell.rs ?? 1))

/**
 * The current schedules of rates, read-only, in a panel that slides out to the
 * left of whatever sits on the right edge (`right` pixels in) - the Add enquiry
 * drawer. The spreadsheet is read on the API, so it keeps its formatting; a tab
 * for each sheet, and a search that narrows to the matching rows.
 *
 * It starts at `width` and widens to fit the sheet, so there's no sideways
 * scrolling, up to `maxWidth` - the room there is.
 */
const SchedulesOfRatesPanel = ({
  right,
  width,
  maxWidth,
  onClose,
}: {
  right: number
  width: number
  maxWidth: number
  onClose: () => void
}) => {
  const [data, setData] = useState<SchedulesOfRates | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [active, setActive] = useState(0)
  const [search, setSearch] = useState('')
  // Mounted closed, then opened on the next frame, so it slides in.
  const [shown, setShown] = useState(false)

  // The widest any sheet has needed so far. It only grows, so the panel
  // doesn't jump about as tabs change or a search narrows the rows.
  const bodyRef = useRef<HTMLDivElement>(null)
  const [needed, setNeeded] = useState(0)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const close = useCallback(() => {
    setShown(false)
    setTimeout(onClose, SLIDE_MS)
  }, [onClose])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close])

  const load = useCallback(async (refresh: boolean) => {
    setLoading(true)
    setError(null)
    try {
      const result = await getSchedulesOfRates(refresh)
      setData(result)
      setActive((a) => (a < result.sheets.length ? a : 0))
    } catch (e) {
      setError(getApiErrorDetails(e, "The schedules of rates couldn't be loaded.").message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(false)
  }, [load])

  const term = search.trim().toLowerCase()

  // While searching: each sheet's matching rows, under its heading. Merges are
  // left out, since the rows around them are gone.
  const matches = useMemo(() => {
    if (!data || !term) return null
    return data.sheets.map((sheet) => {
      const heading = headingCount(sheet)
      return sheet.rows.filter(
        (row, i) => i >= heading && row.cells.some((cell) => cell.t.toLowerCase().includes(term))
      )
    })
  }, [data, term])

  useLayoutEffect(() => {
    const body = bodyRef.current
    const table = body?.querySelector('table')
    if (!body || !table) return
    const measure = () => {
      // The table, plus the vertical scrollbar beside it.
      const fit = Math.ceil(table.getBoundingClientRect().width) + (body.offsetWidth - body.clientWidth) + 2
      setNeeded((n) => Math.max(n, fit))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(table)
    return () => observer.disconnect()
  }, [data, active])

  const panelWidth = Math.min(maxWidth, Math.max(width, needed))

  const sheet = data?.sheets[active]
  const heading = sheet ? headingCount(sheet) : 1
  const bodyRows = sheet ? (matches ? matches[active] : sheet.rows.slice(heading)) : []

  const renderCell = (cell: RatesCell, key: number, header: boolean) => {
    if (cell.x && !matches) return null
    const Tag = header ? 'th' : 'td'
    const style: CSSProperties = {
      ...(cell.n ? { textAlign: 'right' } : {}),
      ...(cell.s !== undefined ? data?.styles[cell.s] : {}),
    }
    return (
      <Tag key={key} style={style} colSpan={matches ? undefined : cell.cs} rowSpan={matches ? undefined : cell.rs}>
        {cell.x ? '' : cell.t}
      </Tag>
    )
  }

  return (
    <aside
      className={`sor-panel${shown ? ' sor-panel--open' : ''}`}
      style={{ right, width: panelWidth }}
      aria-label='Schedules of rates'
    >
      {/* The drawer's own header, so the two line up. */}
      <div className='jobdrawer__header'>
        <h2 className='jobdrawer__title'>Schedules of Rates</h2>
        <button type='button' className='jobdrawer__close' onClick={close} aria-label='Close'>
          <FaTimes />
        </button>
      </div>

      <div className='sor-bar'>
        <div className='sor-search'>
          <FaSearch aria-hidden='true' />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search the rates'
            aria-label='Search the rates'
          />
        </div>
        <span className='sor-meta'>
          {data && `${data.fileName} · as of ${formatTime(data.retrievedAt)} · read only`}
        </span>
        <button type='button' className='sor-refresh' onClick={() => load(true)} disabled={loading} title='Fetch the latest from SharePoint'>
          <FaSyncAlt aria-hidden='true' className={loading ? 'sor-spin' : undefined} />
          Refresh
        </button>
      </div>

      {data && data.sheets.length > 1 && (
        <div className='sor-tabs' role='tablist'>
          {data.sheets.map((s, i) => (
            <button
              key={s.name}
              type='button'
              role='tab'
              aria-selected={i === active}
              className={`sor-tab${i === active ? ' sor-tab--on' : ''}`}
              onClick={() => setActive(i)}
            >
              {s.name}
              {matches && <span className='sor-tab__count'>{matches[i].length}</span>}
            </button>
          ))}
        </div>
      )}

      <div className='sor-body' ref={bodyRef}>
        {error ? (
          <div className='sor-message sor-message--error'>{error}</div>
        ) : !data ? (
          <div className='sor-message'>Loading the schedules of rates…</div>
        ) : !sheet || sheet.rows.length === 0 ? (
          <div className='sor-message'>This sheet is empty.</div>
        ) : (
          <>
            <table className='sor-table'>
              <colgroup>
                {sheet.widths.map((w, i) => (
                  <col key={i} style={{ width: w, minWidth: w }} />
                ))}
              </colgroup>
              <thead>
                {sheet.rows.slice(0, heading).map((row, r) => (
                  <tr key={r} style={row.height ? { height: row.height } : undefined}>
                    {row.cells.map((cell, i) => renderCell(cell, i, true))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {bodyRows.map((row, r) => (
                  <tr key={r} style={row.height ? { height: row.height } : undefined}>
                    {row.cells.map((cell, i) => renderCell(cell, i, false))}
                  </tr>
                ))}
              </tbody>
            </table>
            {matches && bodyRows.length === 0 && (
              <div className='sor-message'>Nothing on this sheet matches “{search.trim()}”.</div>
            )}
          </>
        )}
      </div>
    </aside>
  )
}

export default SchedulesOfRatesPanel
